import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type { Vehicle, PaginatedResponse } from '@/types'

interface VehicleCreate {
  customer_id?: number
  targa: string
  marca: string
  modello: string
  anno?: number
  telaio?: string
  colore?: string
  km_attuali?: number
  note?: string
  // Campi tecnici aggiuntivi
  cilindrata?: string
  kw?: number
  cv?: number
  porte?: number
  carburante?: string
  prima_immatricolazione?: string
}

export const useVehicles = (page: number = 1, size: number = 10, search?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['vehicles', page, size, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        skip: String((page - 1) * size),
        limit: String(size),
      })
      if (search) {
        params.append('search', search)
      }
      const response = await axiosInstance.get<PaginatedResponse<Vehicle>>(
        `${API_ENDPOINTS.VEHICLES}/?${params.toString()}`
      )
      return response.data
    },
    enabled,
  })
}

export const useVehicle = (id: number) => {
  return useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      const response = await axiosInstance.get<Vehicle>(
        `${API_ENDPOINTS.VEHICLES}/${id}`
      )
      return response.data
    },
    enabled: !!id,
  })
}

export const useCustomerVehicles = (customerId: number) => {
  return useQuery({
    queryKey: ['customer-vehicles', customerId],
    queryFn: async () => {
      const response = await axiosInstance.get<{ items: Vehicle[]; total: number }>(
        `${API_ENDPOINTS.VEHICLES}/?customer_history_id=${customerId}&limit=200`
      )
      return response.data.items ?? []
    },
    enabled: !!customerId,
  })
}

export const useCustomerVehicleHistory = (customerId: number | undefined) => {
  return useQuery({
    queryKey: ['customer-vehicle-history', customerId],
    queryFn: async () => {
      const response = await axiosInstance.get<{ items: Vehicle[]; total: number }>(
        `${API_ENDPOINTS.VEHICLES}/?customer_history_id=${customerId}&limit=200`
      )
      return response.data.items ?? []
    },
    enabled: !!customerId,
    staleTime: 60_000,
  })
}

export const useVehicleLastWorkOrder = (vehicleId: number | undefined) => {
  return useQuery({
    queryKey: ['vehicle-last-work-order', vehicleId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.VEHICLES}/${vehicleId}/last-work-order`
      )
      return response.data as {
        work_order_id: number
        numero_scheda: string
        stato: string
        customer_id: number
        customer_nome: string
        is_open: boolean
      } | null
    },
    enabled: !!vehicleId,
  })
}

export const useCreateVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: VehicleCreate) => {
      const response = await axiosInstance.post<Vehicle>(
        `${API_ENDPOINTS.VEHICLES}/`,
        data
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      message.success('Veicolo creato con successo')
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      message.error(typeof detail === 'string' ? detail : 'Errore durante la creazione del veicolo')
    },
  })
}

export const useUpdateVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Vehicle> }) => {
      const response = await axiosInstance.put<Vehicle>(
        `${API_ENDPOINTS.VEHICLES}/${id}`,
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', variables.id] })
      message.success('Veicolo aggiornato con successo')
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      message.error(typeof detail === 'string' ? detail : 'Errore durante l\'aggiornamento del veicolo')
    },
  })
}

export const useTransferVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ vehicleId, customerId, dataFine, note }: {
      vehicleId: number
      customerId: number
      dataFine?: string
      note?: string
    }) => {
      const res = await axiosInstance.post(`${API_ENDPOINTS.VEHICLES}/${vehicleId}/transfer`, {
        customer_id: customerId,
        data_fine: dataFine ?? null,
        note: note ?? null,
      })
      return res.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', variables.vehicleId] })
      message.success('Veicolo trasferito al nuovo cliente')
    },
    onError: (error: any) => {
      const detail = error?.response?.data?.detail
      message.error(typeof detail === 'string' ? detail : 'Errore durante il trasferimento')
    },
  })
}

export const useDeleteVehicle = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API_ENDPOINTS.VEHICLES}/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      message.success('Veicolo eliminato con successo')
    },
    onError: () => {
      message.error('Errore durante l\'eliminazione del veicolo')
    },
  })
}
